import { staticRoutes } from '@/api/apiRoutes';
import { get } from '@/api/http';
import { IRelatedForResort, IWebcamItem } from '@/api/region/types';
import {
  HistoricalSnowfallType,
  IContactInformation,
  IDescriptionsResort,
  IFirstHandReport,
  IResortPageInfo,
  IResortPageSnowInfo,
  IResortReports,
  IResortReviews,
  IResortStrengthsOverview,
  IResortTrailmapPage,
  IReviewsGeneral,
  ISnowfallItem,
  IWeatherInfo,
  ResortWeatherType,
  ReviewsOrderBy,
  IDailyResortLiftTickets,
  ISeasonResortLiftTickets, ISkiResortShop, IResortShortWeatherInfo,
} from '@/api/resort/types';
import { getPortalOptions } from '@/config/getPortalConfig';
import { INews } from '@/api/home/types';
import { WidgetResortData, WidgetRegionData, ResortPhotosData } from '@/components/widget/types';

export const fetchResortLastFHReport = (resortUUID: string) => (
  get<IFirstHandReport>(staticRoutes.resortLastFirstHandReport(resortUUID))
);

export const fetchResortSnowReport = (resortUUID: string) => (
  get<IResortPageSnowInfo>(staticRoutes.resortSnowReport(resortUUID))
);

export const fetchResortShortInfo = (resortUUID: string) => (
  get<IResortPageInfo>(staticRoutes.resortShortInfo(resortUUID))
);

export const fetchResortShortWeatherReport = (resortUUID: string) => (
  get<IResortShortWeatherInfo>(staticRoutes.resortShortWeatherInfo(resortUUID))
);

export const fetchResortSkiRentals = (resortUUID: string) => (
  get<Array<ISkiResortShop>>(staticRoutes.resortSkiRentalsAlpin(resortUUID))
);

export const fetchResortRelated = (resortUUID: string) => (
  get<IRelatedForResort>(staticRoutes.resortRelated(resortUUID))
);

export const fetchResortFeaturedCam = (resortUUID: string) => (
  get<IWebcamItem>(staticRoutes.resortFeaturedCam(resortUUID))
);

export const fetchResortCameras = async (resortUUID: string) => (
  await get<{ data: Array<IWebcamItem> }>(staticRoutes.resortCameras(resortUUID))
).data;

export const fetchWeatherInfo = (
  resortUUID: string,
  type: ResortWeatherType.daily | ResortWeatherType.hourly,
) => (
  get<IWeatherInfo>(staticRoutes.resortWeatherItems(resortUUID, type), type)
);

export const fetchSnowfallInfo = async (
  resortUUID: string,
  type: HistoricalSnowfallType.monthly | HistoricalSnowfallType.annual,
) => (
  await get<{ snowfallItems: Array<ISnowfallItem> }>(
    staticRoutes.resortSnowfallItems(resortUUID, type),
    type,
  )
).snowfallItems;

export const fetchResortTrailMap = (resortUUID: string) => (
  get<IResortTrailmapPage>(staticRoutes.resortTrailMap(resortUUID))
);

export const fetchResortRelatedNews = async (resortUUID: string, summer: boolean = false) => {
  const { hasNews } = getPortalOptions();
  if (hasNews) {
    return get<Array<INews>>(staticRoutes.resortRelatedNews(resortUUID, summer));
  }
  return null;
};

export const fetchResortReports = (regionId: string, page: number, limit: number) => (
  get<IResortReports>(staticRoutes.resortReports(regionId, page, limit), String(page))
);

export const fetchResortReviewsPages = (regionId: string, orderBy: ReviewsOrderBy, page: number) => (
  get<IResortReviews>(
    staticRoutes.resortReviewsPages(regionId, page, orderBy),
    `${page}:${orderBy}`,
  )
);

export const fetchResortReviews = (regionId: string) => (
  get<IReviewsGeneral>(staticRoutes.resortReviews(regionId))
);

export const fetchResortStrengths = (regionId: string) => (
  get<IResortStrengthsOverview>(staticRoutes.resortStrengths(regionId))
);

export const fetchResortDescriptionTabs = (regionId: string) => (
  get<Array<IDescriptionsResort>>(staticRoutes.resortDescriptionTabs(regionId))
);

export const fetchContactInformation = (regionId: string) => (
  get<IContactInformation>(staticRoutes.contactInformation(regionId))
);

export const fetchResortDailyLiftTickets = (resortId: string) => (
  get<IDailyResortLiftTickets>(staticRoutes.resortDailyLiftTickets(resortId))
);

export const fetchResortSeasonLiftTickets = (resortId: string) => (
  get<ISeasonResortLiftTickets>(staticRoutes.resortSeasonLiftTickets(resortId))
);

export const fetchResortsForWidget = (uuids: string) => (
  get<WidgetResortData[]>(staticRoutes.getWidgetResorts(uuids))
);

export const fetchRegionForWidget = (regionId: string, flag?: string) => (
  get<WidgetRegionData>(staticRoutes.getWidgetRegion(regionId, flag))
);

export const fetchResortPhotos = (resortId: string, summer: boolean = false) => (
  get<ResortPhotosData>(staticRoutes.getResortPhotos(resortId, summer))
);
